import React, { useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/pagination'

import safariOption1 from '../../../assets/assets-safari/options/1.jpg'
import safariOption2 from '../../../assets/assets-safari/options/2.jpg'
import safariOption3 from '../../../assets/assets-safari/options/3.jpg'
import safariOption4 from '../../../assets/assets-safari/options/4.jpg'
import safariOption5 from '../../../assets/assets-safari/options/5.jpg'
import safariOption6 from '../../../assets/assets-safari/options/6.jpg'

type SlideData = {
    image: string
    key: string
    region: 'eastAfrica' | 'southernAfrica'
}

const slides: SlideData[] = [
    { image: safariOption1, key: 'kenya', region: 'eastAfrica' },
    { image: safariOption2, key: 'tanzania', region: 'eastAfrica' },
    { image: safariOption3, key: 'rwanda', region: 'eastAfrica' },
    { image: safariOption4, key: 'southAfrica', region: 'southernAfrica' },
    { image: safariOption5, key: 'botswana', region: 'southernAfrica' },
    { image: safariOption6, key: 'zimbabwe', region: 'southernAfrica' },
]

const WhereYoullGoSlider = () => {
    const { t, i18n } = useTranslation('safari')
    const isRu = i18n.language === 'ru'
    const swiperRef = useRef<any>(null)

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!swiperRef.current) return
            const swiper = swiperRef.current.swiper
            const clickedSlide = (e.target as HTMLElement).closest('.swiper-slide')

            if (!clickedSlide) return

            if (clickedSlide.classList.contains('swiper-slide-next')) {
                swiper.slideNext()
            } else if (clickedSlide.classList.contains('swiper-slide-prev')) {
                swiper.slidePrev()
            }
        }

        const sliderElement = swiperRef.current
        if (sliderElement) {
            sliderElement.addEventListener('click', handleClick)
        }

        return () => {
            if (sliderElement) {
                sliderElement.removeEventListener('click', handleClick)
            }
        }
    }, [])

    return (
        <section className="lg:py-[60px] sm:py-10 py-8 bg-white">
            <div className={`px-5 sm:px-6 mx-auto text-center mb-6 sm:mb-8 max-w-[850px]`}>
                <h2 className="section-heading">
                    {t('whereYoullGo.heading')}
                </h2>

                <p className="text-xs sm:text-lg text-[#4A4A4A] font-normal mt-2 sm:mt-4 leading-[20px] sm:leading-[29px]">
                    {t('whereYoullGo.subheading')}
                </p>
            </div>

            <Swiper
                ref={swiperRef}
                modules={[Pagination]}
                slidesPerView="auto"
                spaceBetween={8}
                slidesOffsetBefore={20}
                slidesOffsetAfter={20}
                rewind={true}
                initialSlide={0}
                breakpoints={{
                    640: { slidesOffsetBefore: 24, slidesOffsetAfter: 24 },
                    768: { spaceBetween: 20 },
                    1024: { spaceBetween: 24, slidesOffsetBefore: 40, slidesOffsetAfter: 40 }
                }}
                pagination={{
                    clickable: true,
                    el: '.safari-destinations-pagination'
                }}
            >
                {slides.map((slide) => {
                    const badge = t(`whereYoullGo.regions.${slide.region}`)
                    const heading = t(`whereYoullGo.slides.${slide.key}.heading`)
                    const description = t(`whereYoullGo.slides.${slide.key}.description`)
                    return (
                        <SwiperSlide key={slide.key} className="lg:!w-[688px] sm:!w-[504px] !w-[273px]">
                            <div className="lg:min-h-[350px] sm:min-h-[256px] min-h-[143px] rounded-xl lg:rounded-[20px] lg:p-[27px] sm:p-5 p-3 relative overflow-hidden flex flex-col w-full">
                                <img src={slide.image} alt={heading} className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                                <div className="relative h-full flex flex-col justify-between flex-1">
                                    <div>
                                        <span className="inline-flex items-center h-5 sm:h-8 lg:h-[37px] px-2.5 text-white text-[10px] sm:text-xs lg:text-sm rounded-[4px] sm:rounded-lg lg:rounded-[10px] bg-[#4A90A4]">
                                            {badge}
                                        </span>
                                    </div>

                                    <div>
                                        <h3 className={`lg:text-[32px] sm:text-2xl text-sm font-semibold text-white leading-tight capitalize`}>
                                            {heading.toLowerCase()}
                                        </h3>

                                        <p className={`lg:text-lg sm:text-sm text-xs text-white mt-1 lg:mt-4`}>
                                            {description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    )
                })}
            </Swiper>

            <div className="pagination safari-destinations-pagination" />
        </section>
    )
}

export default WhereYoullGoSlider
